/**
 * Utilities for displaying validation issues with disambiguation
 * (e.g., which destination facility, transporter, or waste line has the issue).
 * Used by both the Oak frontend and the staff-report Lambda.
 */

export interface ValidationIssueForDisplay {
  modelName: string | null | undefined;
  dataName?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** Human-readable label for where each validation issue originates */
function getIssueSourceLabel(modelName: string | null | undefined): string {
  switch (modelName) {
    case "WasteStream":
      return "Waste stream";
    case "WasteManifest":
      return "Destination facility";
    case "WasteTransporter":
      return "Transporter";
    default:
      return modelName ?? "Record";
  }
}

/**
 * Returns a human-readable source label for a validation issue, including
 * disambiguation when multiple objects of the same type (e.g., TSDFs,
 * transporters) could have the issue.
 *
 * Examples:
 * - "Destination facility"
 * - "Destination facility: ABC Disposal (EPA ID: ABC12345)"
 * - "Transporter: manifest 123, transporter XYZ, line # 1"
 */
export function getValidationIssueSourceLabel(issue: ValidationIssueForDisplay): string {
  const baseLabel = getIssueSourceLabel(issue.modelName);

  // Prefer metadata from bulk validation (destination facilities)
  const meta = issue.metadata as
    | { destinationFacilityId?: string; destinationFacilityName?: string }
    | undefined;
  if (meta?.destinationFacilityName || meta?.destinationFacilityId) {
    const name = meta.destinationFacilityName ?? meta.destinationFacilityId;
    const epaId = meta.destinationFacilityId ? ` (EPA ID: ${meta.destinationFacilityId})` : "";
    return `${baseLabel}: ${name}${epaId}`;
  }

  // Use dataName from compliance engine (e.g., transporters already have good getDataName)
  if (issue.dataName?.trim()) {
    return `${baseLabel}: ${issue.dataName}`;
  }

  return baseLabel;
}
